import { z } from "zod";
import { MAX_ACCOUNT_GROUP_DEPTH } from "@common/constants/domains/accounting/accountGroups";
import {
    accountSchema,
    accountTypeSchema,
    ACCOUNT_CODE_TEXT_MAX_LENGTH,
} from "./account.schema";
import { ACCOUNT_GROUP_NAME_MAX_LENGTH } from "./account-group.schema";

const hasDuplicateKeys = (keys: string[]): boolean =>
    new Set(keys).size !== keys.length;

const addDuplicateIssue = (context: z.RefinementCtx, path: string): void => {
    context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path],
        message: "Identities must be unique",
    });
};

const entityIdSchema = z.number().int().positive();
const groupNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(ACCOUNT_GROUP_NAME_MAX_LENGTH);
// Blank account names are refused in the editor, so they never reach here.
const accountNameSchema = z.string().trim().min(1);
const codeTextSchema = z
    .string()
    .trim()
    .min(1)
    .max(ACCOUNT_CODE_TEXT_MAX_LENGTH)
    .nullable();
// One gesture holds the workspace-wide account-group lock for as long as its
// arrays are long, so every write body is bounded.
const BODY_ARRAY_MAX = 500;

// A group is addressed by its ancestor names from the type root down. `null` is
// the type root itself, never an empty path.
export const groupNamePathSchema = z
    .array(groupNameSchema)
    .min(1)
    .max(MAX_ACCOUNT_GROUP_DEPTH);

const nestedParentGroupSchema = z
    .object({ namePath: groupNamePathSchema })
    .nullable();

// Top-level refs carry their own entity and type; nested ones inherit both from
// the enclosing `created[]` / `rows[]` item.
const groupRefSchema = z.object({
    entityId: entityIdSchema,
    type: accountTypeSchema,
    namePath: groupNamePathSchema,
});

const accountRefSchema = z.object({
    entityId: entityIdSchema,
    accountId: z.string().uuid(),
});

const scopeInsertPositionSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("first") }),
    z.object({
        kind: z.literal("afterAccount"),
        afterAccountId: z.string().uuid(),
    }),
    z.object({ kind: z.literal("append") }),
]);

const groupRefKey = (ref: z.infer<typeof groupRefSchema>): string =>
    JSON.stringify([ref.entityId, ref.type, ref.namePath]);

const accountRefKey = (ref: z.infer<typeof accountRefSchema>): string =>
    JSON.stringify([ref.entityId, ref.accountId]);

export const chartEditorUpdateAccountBodySchema = z
    .object({
        accounts: z.array(accountRefSchema).min(1).max(BODY_ARRAY_MAX),
        name: accountNameSchema.optional(),
        codeText: codeTextSchema.optional(),
    })
    .superRefine((body, context) => {
        if (body.name === undefined && body.codeText === undefined) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["name"],
                message: "An update needs a name or a codeText",
            });
        }
        if (hasDuplicateKeys(body.accounts.map(accountRefKey))) {
            addDuplicateIssue(context, "accounts");
        }
    });

export const chartEditorAddAccountBodySchema = z
    .object({
        accountType: accountTypeSchema,
        name: accountNameSchema,
        created: z
            .array(
                z.object({
                    entityId: entityIdSchema,
                    tempId: z.string().min(1).max(255),
                    parentGroup: nestedParentGroupSchema,
                    position: scopeInsertPositionSchema,
                    // Explicit `null` keeps the create from storing the
                    // allocated numeric code as the person's code.
                    codeText: codeTextSchema,
                }),
            )
            .min(1)
            .max(BODY_ARRAY_MAX),
    })
    .superRefine((body, context) => {
        if (hasDuplicateKeys(body.created.map((row) => String(row.entityId)))) {
            addDuplicateIssue(context, "created");
        }
    });

export const chartEditorAddGroupBodySchema = z
    .object({
        accountType: accountTypeSchema,
        name: groupNameSchema,
        created: z
            .array(
                z.object({
                    entityId: entityIdSchema,
                    parentGroup: nestedParentGroupSchema,
                    position: scopeInsertPositionSchema,
                }),
            )
            .min(1)
            .max(BODY_ARRAY_MAX),
    })
    .superRefine((body, context) => {
        if (hasDuplicateKeys(body.created.map((row) => String(row.entityId)))) {
            addDuplicateIssue(context, "created");
        }
    });

export const chartEditorRenameGroupBodySchema = z
    .object({
        name: groupNameSchema,
        groups: z.array(groupRefSchema).min(1).max(BODY_ARRAY_MAX),
    })
    .superRefine((body, context) => {
        if (hasDuplicateKeys(body.groups.map(groupRefKey))) {
            addDuplicateIssue(context, "groups");
        }
    });

export const chartEditorDeleteBodySchema = z
    .object({
        accounts: z.array(accountRefSchema).max(BODY_ARRAY_MAX),
        groups: z.array(groupRefSchema).max(BODY_ARRAY_MAX),
    })
    .superRefine((body, context) => {
        if (body.accounts.length === 0 && body.groups.length === 0) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Delete needs at least one account or group",
            });
        }
        if (hasDuplicateKeys(body.accounts.map(accountRefKey))) {
            addDuplicateIssue(context, "accounts");
        }
        if (hasDuplicateKeys(body.groups.map(groupRefKey))) {
            addDuplicateIssue(context, "groups");
        }
    });

export const chartEditorSetAccountDisabledBodySchema = z
    .object({
        isDisabled: z.boolean(),
        accounts: z.array(accountRefSchema).min(1).max(BODY_ARRAY_MAX),
    })
    .superRefine((body, context) => {
        if (hasDuplicateKeys(body.accounts.map(accountRefKey))) {
            addDuplicateIssue(context, "accounts");
        }
    });

// `append` cannot be paired with a before/after edge, so `position` lives on the
// neighbour rather than beside it.
const moveNeighbourSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("account"),
        accountId: z.string().uuid(),
        position: z.enum(["before", "after"]),
    }),
    z.object({
        kind: z.literal("group"),
        namePath: groupNamePathSchema,
        position: z.enum(["before", "after"]),
    }),
    z.object({ kind: z.literal("append") }),
]);

const moveRowBaseShape = {
    entityId: entityIdSchema,
    targetType: accountTypeSchema,
    destinationParentGroup: nestedParentGroupSchema,
    neighbour: moveNeighbourSchema,
};

const moveRowSchema = z.discriminatedUnion("kind", [
    z.object({
        ...moveRowBaseShape,
        kind: z.literal("account"),
        accountId: z.string().uuid(),
    }),
    z.object({
        ...moveRowBaseShape,
        kind: z.literal("group"),
        namePath: groupNamePathSchema,
        // Lookup is by current Type; targetType is the destination.
        sourceType: accountTypeSchema,
    }),
]);

export type ChartEditorMoveRow = z.infer<typeof moveRowSchema>;
export type ChartEditorMoveNeighbour = z.infer<typeof moveNeighbourSchema>;

const moveRowKey = (row: ChartEditorMoveRow): string =>
    row.kind === "account"
        ? JSON.stringify(["account", row.accountId])
        : JSON.stringify(["group", row.entityId, row.targetType, row.namePath]);

export const chartEditorMoveBodySchema = z
    .object({
        rows: z.array(moveRowSchema).min(1).max(BODY_ARRAY_MAX),
    })
    .superRefine((body, context) => {
        if (hasDuplicateKeys(body.rows.map(moveRowKey))) {
            addDuplicateIssue(context, "rows");
        }
    });

export const chartEditorCreatedAccountSchema = accountSchema.extend({
    entityId: entityIdSchema,
});

export const chartEditorAddAccountResponseSchema = z.object({
    accounts: z.array(chartEditorCreatedAccountSchema),
});

export type ChartEditorUpdateAccountBody = z.infer<
    typeof chartEditorUpdateAccountBodySchema
>;
export type ChartEditorAddAccountBody = z.infer<
    typeof chartEditorAddAccountBodySchema
>;
export type ChartEditorAddGroupBody = z.infer<
    typeof chartEditorAddGroupBodySchema
>;
export type ChartEditorRenameGroupBody = z.infer<
    typeof chartEditorRenameGroupBodySchema
>;
export type ChartEditorDeleteBody = z.infer<typeof chartEditorDeleteBodySchema>;
export type ChartEditorSetAccountDisabledBody = z.infer<
    typeof chartEditorSetAccountDisabledBodySchema
>;
export type ChartEditorMoveBody = z.infer<typeof chartEditorMoveBodySchema>;
export type ChartEditorAddAccountResponse = z.infer<
    typeof chartEditorAddAccountResponseSchema
>;
export type ChartEditorGroupRef = z.infer<typeof groupRefSchema>;
export type ChartEditorAccountRef = z.infer<typeof accountRefSchema>;
export type ChartEditorCreatedAccount = z.infer<
    typeof chartEditorCreatedAccountSchema
>;
